import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { assetsAPI } from '@/lib/api-client'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AssetFormDialog } from './assets/AssetFormDialog'
import {
  Plus,
  Search,
  Package,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Archive,
  Tag,
  CheckCircle,
} from 'lucide-react'

const STATUS_LABELS: Record<string, string> = {
  EM_POSSE: 'Em Posse',
  A_VENDA: 'A Venda',
  VENDIDO: 'Vendido',
  UTILIZADO: 'Utilizado',
}

const STATUS_COLORS: Record<string, string> = {
  EM_POSSE: 'bg-blue-100 text-blue-700',
  A_VENDA: 'bg-amber-100 text-amber-700',
  VENDIDO: 'bg-green-100 text-green-700',
  UTILIZADO: 'bg-purple-100 text-purple-700',
}

const CATEGORY_LABELS: Record<string, string> = {
  VEHICLE: 'Veiculo',
  PROPERTY: 'Imovel',
  LAND: 'Terreno',
  HOUSE: 'Casa',
  OTHER: 'Outro',
}

const ORIGIN_LABELS: Record<string, string> = {
  PERMUTA: 'Permuta',
  MANUAL: 'Manual',
}

const ORIGIN_COLORS: Record<string, string> = {
  PERMUTA: 'bg-amber-100 text-amber-700',
  MANUAL: 'bg-neutral-100 text-neutral-700',
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

export function Assets() {
  const navigate = useNavigate()
  const canCreate = usePermission('assets:create')

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [originFilter, setOriginFilter] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['assets', { page, search, status: statusFilter, category: categoryFilter, origin: originFilter }],
    queryFn: () =>
      assetsAPI.list({
        page,
        limit: 9,
        search: search || undefined,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        origin: originFilter || undefined,
      }),
  })

  const assets = data?.data || []
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 }
  const summary = data?.summary

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const hasFilters = search || statusFilter || categoryFilter || originFilter

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Ativos</h1>
          <p className="mt-1 text-neutral-600">
            Gerencie os ativos da empresa
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Ativo
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-3 pt-6">
              <Package className="h-8 w-8 text-neutral-400" />
              <div>
                <p className="text-2xl font-bold text-neutral-900">{summary.total}</p>
                <p className="text-xs text-neutral-500">Total de Ativos</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 pt-6">
              <Archive className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-neutral-900">{summary.emPosse}</p>
                <p className="text-xs text-neutral-500">Em Posse</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 pt-6">
              <Tag className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold text-neutral-900">{summary.aVenda}</p>
                <p className="text-xs text-neutral-500">A Venda</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 pt-6">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-neutral-900">{summary.liquidados}</p>
                <p className="text-xs text-neutral-500">Liquidados</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar por nome ou descricao..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>

        <Select
          value={statusFilter || 'ALL'}
          onValueChange={(v) => { setStatusFilter(v === 'ALL' ? '' : v); setPage(1) }}
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="EM_POSSE">Em Posse</SelectItem>
            <SelectItem value="A_VENDA">A Venda</SelectItem>
            <SelectItem value="VENDIDO">Vendido</SelectItem>
            <SelectItem value="UTILIZADO">Utilizado</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={categoryFilter || 'ALL'}
          onValueChange={(v) => { setCategoryFilter(v === 'ALL' ? '' : v); setPage(1) }}
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas</SelectItem>
            <SelectItem value="VEHICLE">Veiculo</SelectItem>
            <SelectItem value="PROPERTY">Imovel</SelectItem>
            <SelectItem value="LAND">Terreno</SelectItem>
            <SelectItem value="HOUSE">Casa</SelectItem>
            <SelectItem value="OTHER">Outro</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={originFilter || 'ALL'}
          onValueChange={(v) => { setOriginFilter(v === 'ALL' ? '' : v); setPage(1) }}
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Origem" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todas</SelectItem>
            <SelectItem value="PERMUTA">Permuta</SelectItem>
            <SelectItem value="MANUAL">Manual</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
          <Package className="h-16 w-16 text-neutral-300" />
          <h3 className="mt-4 text-xl font-medium text-neutral-900">
            {hasFilters ? 'Nenhum ativo encontrado' : 'Nenhum ativo cadastrado'}
          </h3>
          <p className="mt-2 text-neutral-500">
            {hasFilters
              ? 'Tente ajustar os filtros de busca.'
              : 'Comece cadastrando seu primeiro ativo ou registre uma venda com permuta.'}
          </p>
          {canCreate && !hasFilters && (
            <Button className="mt-6" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Cadastrar Primeiro Ativo
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Asset Cards Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {assets.map((asset: any) => (
              <Card
                key={asset.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/assets/${asset.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <Package className="h-5 w-5 text-primary shrink-0" />
                      <CardTitle className="text-base line-clamp-1">
                        {asset.name}
                      </CardTitle>
                    </div>
                    <Badge
                      variant="outline"
                      className={STATUS_COLORS[asset.status] || ''}
                    >
                      {STATUS_LABELS[asset.status] || asset.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {CATEGORY_LABELS[asset.category] || asset.category}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={ORIGIN_COLORS[asset.origin] || ''}
                    >
                      {ORIGIN_LABELS[asset.origin] || asset.origin}
                    </Badge>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-neutral-500">Aquisicao</span>
                      <span className="font-medium text-neutral-900">
                        {formatCurrency(asset.acquisitionValue)}
                      </span>
                    </div>
                    {asset.marketValue && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-500">Mercado</span>
                        <span className="font-medium text-neutral-900">
                          {formatCurrency(asset.marketValue)}
                        </span>
                      </div>
                    )}
                  </div>

                  {asset.project && (
                    <p className="text-xs text-neutral-400 truncate">
                      Projeto: {asset.project.name}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">
                Mostrando {assets.length} de {pagination.total} ativos
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Anterior
                </Button>
                <span className="text-sm text-neutral-600">
                  {page} / {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Proximo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Dialog */}
      <AssetFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  )
}
